import { SRGBColorSpace, Texture, TextureLoader } from 'three';
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js';

import { TextureType } from '../../../../model/enums';
import type { TextureInfo } from '../../../../model/texture-info';
import { getAnisotropyForQuality, getEffectiveTextureQuality } from '../../../../hooks/use-three-kitchen/quality';
import type { TextureManager } from '../texture-manager.class';
import type { TextureLoadCallbacks } from '../types';
import {
	configureSkyTexture,
	configureStandardTexture,
	configureMapTexture,
	countTexturesToLoad
} from '../helpers/texture-config.helper';
import type { Ktx2Support } from '../helpers/ktx2-support';
import { loadWithKtx2Fallback } from '../helpers/load-with-ktx2-fallback.helper';
import { loadWithStallRetry } from '../helpers/load-with-stall-retry.helper';

export interface LoadTexturesParams {
	textureInfos: TextureInfo[];
	callbacks?: TextureLoadCallbacks;
}

/**
 * Load textures from TextureInfo array.
 * Tracks completion via a per-call counter rather than a Three.js LoadingManager:
 * KTX2Loader uses its own internal LoadingManager for the underlying fetch, so a
 * per-batch manager wired to TextureLoader/RGBELoader never observes KTX2 loads
 * and onLoad would never fire when KTX2 is the active path.
 */
export const loadTexturesCfn = (self: TextureManager, params: LoadTexturesParams): Promise<void> => {
	const { textureInfos, callbacks = {} } = params;
	const config = self.config;
	const textures = self.textures;
	const normalMaps = self.normalMaps;
	const alphaMaps = self.alphaMaps;
	const displacementMaps = self.displacementMaps;

	return new Promise<void>((resolve) => {
		const cachedTextureLoader = self.textureLoader;
		const cachedHdrLoader = self.hdrLoader;

		const totalCount = countTexturesToLoad(textureInfos, textures, alphaMaps, normalMaps, displacementMaps);

		if (totalCount === 0) {
			self.createMaterials(textureInfos);
			callbacks.onComplete?.();
			resolve();
			return;
		}

		let settled = 0;
		let resolved = false;
		const onSettled = (): void => {
			if (resolved) return;
			settled++;
			callbacks.onProgress?.(settled, totalCount);
			if (settled >= totalCount) {
				resolved = true;
				self.createMaterials(textureInfos);
				callbacks.onComplete?.();
				resolve();
			}
		};

		const ktx2Support = self.ktx2Support;
		for (const textureInfo of textureInfos) {
			loadMainTexture(textureInfo, cachedTextureLoader, cachedHdrLoader, config, textures, ktx2Support, onSettled, self);
			loadAlphaMap(textureInfo, cachedTextureLoader, cachedHdrLoader, config, alphaMaps, ktx2Support, onSettled, self);
			loadNormalMap(textureInfo, cachedTextureLoader, cachedHdrLoader, config, normalMaps, ktx2Support, onSettled, self);
			loadDisplacementMap(textureInfo, cachedTextureLoader, cachedHdrLoader, config, displacementMaps, ktx2Support, onSettled, self);
		}
	});
};

const loadMainTexture = (
	textureInfo: TextureInfo,
	textureLoader: TextureLoader,
	hdrLoader: RGBELoader,
	config: any,
	textures: Map<string, Texture>,
	ktx2Support: Ktx2Support,
	onSettled: () => void,
	self: TextureManager
): void => {
	if (textures.has(textureInfo.key)) return;
	if (!textureInfo.texture) return;

	if (textureInfo.handleAsHdr) {
		const path = `${config.basePath}textures/${textureInfo.texture}`;
		loadWithKtx2Fallback({
			originalUrl: path,
			ktx2Support,
			textureLoader,
			hdrLoader,
			onLoad: (texture: Texture) => {
				texture.name = textureInfo.key;
				textures.set(textureInfo.key, texture);
				onSettled();
			},
			onError: (error) => {
				console.error('[loadMainTexture] HDR error:', textureInfo.key, error);
				self.recordFailedKey(textureInfo.key);
				onSettled();
			}
		});
		return;
	}

	// Use KTX2 compressed variant if available
	const useKtx2 = ktx2Support.isEnabled && (textureInfo.ktx2Path || !textureInfo.handleAsHdr);
	const ktx2Loader = useKtx2 ? ktx2Support.loader : null;
	const textureFilename = ktx2Loader
		? (textureInfo.ktx2Path ?? ktx2Support.getKtx2Filename(textureInfo.texture))
		: textureInfo.texture;
	const path = `${config.basePath}textures/${textureFilename}`;

	const onTextureLoaded = (texture: Texture) => {
		texture.name = textureInfo.key;
		texture.colorSpace = SRGBColorSpace;
		const effectiveQuality = getEffectiveTextureQuality(config.textureQuality, config.renderQuality);
		texture.anisotropy = getAnisotropyForQuality(effectiveQuality, config.renderer);

		if (textureInfo.textureType === TextureType.Sky) {
			configureSkyTexture(texture);
		} else {
			configureStandardTexture(texture, textureInfo, effectiveQuality, config.renderer);
		}

		textures.set(textureInfo.key, texture);
	};

	if (ktx2Loader) {
		loadWithStallRetry({
			url: path,
			label: `main-ktx2 ${textureInfo.key}`,
			load: (url, ol, oe) => ktx2Loader.load(url, ol, undefined, oe),
			onLoad: (texture) => {
				onTextureLoaded(texture);
				onSettled();
			},
			onError: (error) => {
				console.warn('[loadMainTexture] KTX2 failed, falling back to standard:', textureInfo.key, error);
				const fallbackPath = `${config.basePath}textures/${textureInfo.texture}`;
				loadWithStallRetry({
					url: fallbackPath,
					label: `main-fallback ${textureInfo.key}`,
					load: (url, ol, oe) => textureLoader.load(url, ol, undefined, oe),
					onLoad: (texture) => {
						onTextureLoaded(texture);
						onSettled();
					},
					onError: (fallbackError) => {
						console.error('[loadMainTexture] Fallback error:', textureInfo.key, fallbackError);
						self.recordFailedKey(textureInfo.key);
						onSettled();
					}
				});
			}
		});
	} else {
		loadWithStallRetry({
			url: path,
			label: `main ${textureInfo.key}`,
			load: (url, ol, oe) => textureLoader.load(url, ol, undefined, oe),
			onLoad: (texture) => {
				onTextureLoaded(texture);
				onSettled();
			},
			onError: (error) => {
				console.error('[loadMainTexture] Texture error:', textureInfo.key, error);
				self.recordFailedKey(textureInfo.key);
				onSettled();
			}
		});
	}
};

const loadAlphaMap = (
	textureInfo: TextureInfo,
	textureLoader: TextureLoader,
	hdrLoader: RGBELoader,
	config: any,
	alphaMaps: Map<string, Texture>,
	ktx2Support: Ktx2Support,
	onSettled: () => void,
	self: TextureManager
): void => {
	if (alphaMaps.has(textureInfo.key)) return;
	if (!textureInfo.alphaMapPath) return;

	const path = `${config.basePath}textures/${textureInfo.alphaMapPath}`;
	loadWithKtx2Fallback({
		originalUrl: path,
		ktx2Support,
		textureLoader,
		hdrLoader,
		onLoad: (texture: Texture) => {
			configureMapTexture(texture, textureInfo, getEffectiveTextureQuality(config.textureQuality, config.renderQuality), config.renderer);
			alphaMaps.set(textureInfo.key, texture);
			onSettled();
		},
		onError: (error) => {
			console.error('[loadAlphaMap] error:', textureInfo.key, error);
			self.recordFailedKey(textureInfo.key);
			onSettled();
		}
	});
};

const loadNormalMap = (
	textureInfo: TextureInfo,
	textureLoader: TextureLoader,
	hdrLoader: RGBELoader,
	config: any,
	normalMaps: Map<string, Texture>,
	ktx2Support: Ktx2Support,
	onSettled: () => void,
	self: TextureManager
): void => {
	if (normalMaps.has(textureInfo.key)) return;
	if (!textureInfo.normalMapPath) return;

	const path = `${config.basePath}textures/${textureInfo.normalMapPath}`;
	loadWithKtx2Fallback({
		originalUrl: path,
		ktx2Support,
		textureLoader,
		hdrLoader,
		onLoad: (texture: Texture) => {
			configureMapTexture(texture, textureInfo, getEffectiveTextureQuality(config.textureQuality, config.renderQuality), config.renderer);
			normalMaps.set(textureInfo.key, texture);
			onSettled();
		},
		onError: (error) => {
			console.error('[loadNormalMap] error:', textureInfo.key, error);
			self.recordFailedKey(textureInfo.key);
			onSettled();
		}
	});
};

const loadDisplacementMap = (
	textureInfo: TextureInfo,
	textureLoader: TextureLoader,
	hdrLoader: RGBELoader,
	config: any,
	displacementMaps: Map<string, Texture>,
	ktx2Support: Ktx2Support,
	onSettled: () => void,
	self: TextureManager
): void => {
	if (displacementMaps.has(textureInfo.key)) return;
	if (!textureInfo.displacementMapPath) return;

	const path = `${config.basePath}textures/${textureInfo.displacementMapPath}`;
	loadWithKtx2Fallback({
		originalUrl: path,
		ktx2Support,
		textureLoader,
		hdrLoader,
		onLoad: (texture: Texture) => {
			configureMapTexture(texture, textureInfo, getEffectiveTextureQuality(config.textureQuality, config.renderQuality), config.renderer);
			displacementMaps.set(textureInfo.key, texture);
			onSettled();
		},
		onError: (error) => {
			console.error('[loadDisplacementMap] error:', textureInfo.key, error);
			self.recordFailedKey(textureInfo.key);
			onSettled();
		}
	});
};
